import { useTranslation } from 'react-i18next';

import { Icon, ICONS } from './icon';
import { NavLink } from './nav-link';
import { localePath } from '../lib/format';
import { useLocale } from '@todo/core/i18n/use-locale';

import css from './listing-card.module.css';

type Props = {
  href: string;
  imageUrl: string | null;
  /** A larger copy of the image for the desktop layout (from 1024 px). */
  imageDesktopUrl?: string | null;
  /** Describes the photo; omitted for a row's image (the title says it all). */
  imageAlt?: string;
  category: string;
  title: string;
  /** A sentence under the title (the design's sample cards). */
  text?: string;
  /** Short facts under the title (rating, duration, boat type…). */
  facts?: string[];
  /** Bottom-left line, e.g. "from €120"; else `place` with a pin. */
  price?: string | null;
  place?: string;
};

// The design's card ("Featured This Week"): image, save heart, category,
// serif title, text, location or price, round arrow. The whole card is one
// link — the title's anchor is stretched over it — so a screen reader hears it
// once. The heart leads to the "Saved" page, a "coming soon" stub until
// saving is decided (docs/monorepo-plan.md, stage 5.5).
export function ListingCard({
  href,
  imageUrl,
  imageDesktopUrl,
  imageAlt = '',
  category,
  title,
  text,
  facts,
  price,
  place,
}: Props) {
  const { t } = useTranslation();
  const { locale } = useLocale();

  // width/height: the photos are 3:2; the CSS sets the box (full width, 210 px
  // tall), so the attributes only give the browser an aspect ratio up front.
  const image = imageUrl ? (
    <img className={css.image} src={imageUrl} alt={imageAlt} width={480} height={320} loading="lazy" decoding="async" />
  ) : null;

  return (
    <article className={css.card}>
      {image && imageDesktopUrl ? (
        <picture className={css.picture}>
          <source media="(min-width: 1024px)" srcSet={imageDesktopUrl} />
          {image}
        </picture>
      ) : image ? (
        image
      ) : (
        <div className={`${css.image} ${css.noImage}`} aria-hidden="true" />
      )}
      <NavLink href={localePath(locale, 'saved')} aria-label={t('a11y.save', { title })} className={css.save}>
        <Icon d={ICONS.heart} size={24} strokeWidth={1.5} className={css.saveIcon} />
      </NavLink>
      <div className={css.body}>
        <span className={css.category}>{category}</span>
        <h3 className={css.title}>
          <NavLink href={href} className={css.link}>
            {title}
          </NavLink>
        </h3>
        {text ? <p className={css.text}>{text}</p> : null}
        {facts?.length ? <p className={`${css.text} ${css.facts}`}>{facts.join(' · ')}</p> : null}
        <div className={css.footer}>
          {price ? (
            <span className={css.price}>{price}</span>
          ) : place ? (
            <span className={css.place}>
              <Icon d={ICONS.pin} size={14} className={css.placeIcon} />
              {place}
            </span>
          ) : (
            <span />
          )}
          <span className={css.arrow} aria-hidden="true">
            <Icon d={ICONS.arrowRight} size={15} strokeWidth={1.8} className={css.arrowIcon} />
          </span>
        </div>
      </div>
    </article>
  );
}
